import React from "react"
import { ChevronDown, X } from "lucide-react"

const FONT = '"Plus Jakarta Sans", sans-serif'

const experienceOptions = ["Fresher", "0–2 Years", "2–5 Years", "5–10 Years", "10+ Years"]
const workModeOptions   = ["Work from office", "Remote", "Hybrid"]
const salaryRanges      = ["0–3 L", "3–6 L", "6–10 L", "10–15 L", "15–25 L", "25–50 L", "50 L+"]
const locationOptions   = ["Bengaluru", "Hyderabad", "Delhi / NCR", "Pune", "Mumbai"]

function FilterSection({ label, open, onToggle, children }) {
  return (
    <div className="border-b border-zinc-100 last:border-0 py-4">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between mb-0 cursor-pointer"
      >
        <span className="text-[13px] font-semibold text-zinc-900" style={{ fontFamily: FONT }}>{label}</span>
        <ChevronDown
          size={15}
          className={`text-zinc-400 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}
        />
      </button>
      {open && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  )
}

function CheckItem({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="w-4 h-4 rounded border-zinc-300 accent-zinc-900 cursor-pointer flex-shrink-0"
      />
      <span
        className="text-[13px] text-zinc-600 group-hover:text-zinc-900 transition-colors"
        style={{ fontFamily: FONT }}
      >
        {label}
      </span>
    </label>
  )
}

const FilterSidebar = ({
  selectedWorkMode,
  setSelectedWorkMode,
  selectedLocations,
  setSelectedLocations,
  minSalary,
  setMinSalary,
  clearFilters,
  isMobile = false,
}) => {
  const [openExperience, setOpenExperience] = React.useState(true)
  const [openLocation,   setOpenLocation]   = React.useState(true)
  const [openWorkMode,   setOpenWorkMode]   = React.useState(true)
  const [openSalary,     setOpenSalary]     = React.useState(false)

  const [localExperience, setLocalExperience] = React.useState({})
  const [localWorkModes,  setLocalWorkModes]  = React.useState({})
  const [localLocations,  setLocalLocations]  = React.useState({})
  const [localSalary,     setLocalSalary]     = React.useState({})

  const hasAny =
    Object.values(localExperience).some(Boolean) ||
    Object.values(localWorkModes).some(Boolean)  ||
    Object.values(localLocations).some(Boolean)  ||
    Object.values(localSalary).some(Boolean)

  function handleClear() {
    setLocalExperience({})
    setLocalWorkModes({})
    setLocalLocations({})
    setLocalSalary({})
    clearFilters()
  }

  return (
    <div
      className={`${isMobile ? "w-full" : "w-60"} flex-shrink-0`}
      style={{ fontFamily: FONT }}
    >
      <div className={`${isMobile ? "p-5" : "bg-white rounded-xl border border-zinc-200 p-5"}`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-[13px] font-bold text-zinc-900 tracking-[.04em] uppercase" style={{ fontFamily: FONT }}>
            Filter
          </span>
          {hasAny && (
            <button
              onClick={handleClear}
              className="flex items-center gap-1 text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer"
              style={{ fontFamily: FONT }}
            >
              Clear <X size={12} />
            </button>
          )}
        </div>

        {/* Experience */}
        <FilterSection label="Experience" open={openExperience} onToggle={() => setOpenExperience(v => !v)}>
          {experienceOptions.map(opt => (
            <CheckItem
              key={opt}
              label={opt}
              checked={localExperience[opt] || false}
              onChange={e => setLocalExperience(p => ({ ...p, [opt]: e.target.checked }))}
            />
          ))}
        </FilterSection>

        {/* Location */}
        <FilterSection label="Location" open={openLocation} onToggle={() => setOpenLocation(v => !v)}>
          {locationOptions.map(opt => (
            <CheckItem
              key={opt}
              label={opt}
              checked={localLocations[opt] || false}
              onChange={e => setLocalLocations(p => ({ ...p, [opt]: e.target.checked }))}
            />
          ))}
        </FilterSection>

        {/* Work mode */}
        <FilterSection label="Work mode" open={openWorkMode} onToggle={() => setOpenWorkMode(v => !v)}>
          {workModeOptions.map(opt => (
            <CheckItem
              key={opt}
              label={opt}
              checked={localWorkModes[opt] || false}
              onChange={e => setLocalWorkModes(p => ({ ...p, [opt]: e.target.checked }))}
            />
          ))}
        </FilterSection>

        {/* Salary */}
        <FilterSection label="Salary" open={openSalary} onToggle={() => setOpenSalary(v => !v)}>
          {salaryRanges.map(opt => (
            <CheckItem
              key={opt}
              label={opt}
              checked={localSalary[opt] || false}
              onChange={e => setLocalSalary(p => ({ ...p, [opt]: e.target.checked }))}
            />
          ))}
        </FilterSection>
      </div>
    </div>
  )
}

export default FilterSidebar
